import SaveRoutePlanningBoard from '@/server/saveRoutePlanningBoard'
import calculateDistance from '@/utils/calculateDistance'
import { sortCustomers } from '@/utils/customerHelpers'
import { configureStore, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ReactFlowJsonObject } from '@xyflow/react'
import { set } from 'better-auth'

export type NotifyType = 'success' | 'info' | 'warning' | 'error'

type notification = {
    id: number
    type: NotifyType
    message: string
}

const initialStateImage = { imageVersion: Date.now(), imageURL: '' }
const initialStateSidebar = { isExpanded: false }
const initialStateNotifyContainer = { notifyCount: 0, notifications: [] as notification[] }
const initialStateActiveCustomer = { activeCustomerId: null as string | null }
const initialRoutePlanningBoardState = {
    tmp_customers: {} as Record<string, customer[]>,
    editing_board: null as RoutePlanningBoard | null,
    editing_board_uuid: null as string | null,
}

/* const initialDummyRoutes: route[] = [
    {
        id: 'route-1',
        type: 'stopp',
        start: {
            customer_number: 'CUST001',
            company_name: 'Company A',
            customer_zip_code: '12345',
            customer_city: 'City A',
            lat: 48.67013,
            lng: 7.94915,
            departureTime: new Date().toISOString(),
        },
        end: {
            customer_number: 'CUST002',
            company_name: 'Company B',
            customer_zip_code: '67890',
            customer_city: 'City B',
            lat: 48.69013,
            lng: 7.95915,
            arrivalTime: new Date(new Date().getTime() + 3600 * 1000).toISOString(),
        },
        distance: 15000,
        travelTime: 1800,
        bufferTime: 600,
        smartBufferTime: 0,
        routePoints: [],
        stayDuration: 900,
        routeEvents: ['ROAD_WORK'],
    },
    {
        id: 'route-2',
        type: 'hotel',
        start: {
            customer_number: 'CUST002', // Same as end of previous route
            company_name: 'Company B',
            customer_zip_code: '67890',
            customer_city: 'City B',
            lat: 48.69013,
            lng: 7.95915,
            departureTime: new Date(new Date().getTime() + 5400 * 1000).toISOString(),
        },
        end: {
            customer_number: 'CUST003',
            company_name: 'Hotel C',
            customer_zip_code: '54321',
            customer_city: 'City C',
            lat: 48.71013,
            lng: 7.96915,
            arrivalTime: new Date(new Date().getTime() + 7200 * 1000).toISOString(),
        },
        distance: 20000,
        travelTime: 2400,
        bufferTime: 900,
        smartBufferTime: 0,
        routePoints: [],
        stayDuration: 28800,
        routeEvents: ['ROAD_WORK'],
    },
]
 */
const imageSlice = createSlice({
    name: 'image',
    initialState: initialStateImage,
    reducers: {
        updateImageVersion(state) {
            state.imageVersion = Date.now()
        },
        updateImageURL(state, action) {
            state.imageURL = action.payload.newImageURL
        },
    },
})

const sidebarSlice = createSlice({
    name: 'sidebar',
    initialState: initialStateSidebar,
    reducers: {
        toggleSidebar(state) {
            state.isExpanded = !state.isExpanded
        },
    },
})

const notifyContainerSlice = createSlice({
    name: 'notifyContainer',
    initialState: initialStateNotifyContainer,
    reducers: {
        addNotification(state, action) {
            const exists = state.notifications.find((notif) => notif.message === action.payload.message && notif.type === action.payload.type)
            if (exists) {
                console.log('Notification already exists:', exists)
                return
            }

            action.payload.id = Date.now()
            state.notifications.push(action.payload)
            state.notifyCount += 1
        },
        removeNotification(state, action) {
            state.notifications = state.notifications.filter((notif) => notif.id !== action.payload)
            state.notifyCount -= 1
        },
    },
})

const allCustomersSlice = createSlice({
    name: 'customers',
    initialState: { items: [] as customer[], activeCustomerId: null as string | null },
    reducers: {
        setCustomers: (state, action: PayloadAction<customer[]>) => {
            state.items = action.payload
        },
        deleteCustomerLocal: (state, action: PayloadAction<string>) => {
            state.items = state.items.filter((c) => c.customer_number !== action.payload)
        },
        addCustomerLocal: (state, action: PayloadAction<customer>) => {
            state.items.push(action.payload)
            state.items = sortCustomers(state.items, 'company_name')
        },
        toggleFavoriteLocal: (state, action: PayloadAction<string>) => {
            const customer = state.items.find((c) => c.customer_number === action.payload)
            if (customer) customer.favorite = !customer.favorite
            state.items = sortCustomers(state.items, 'company_name')
        },
        setActiveCustomer(state, action) {
            state.activeCustomerId = action.payload
        },
    },
})

const customerMapSlice = createSlice({
    name: 'customerMapState',
    initialState: {
        searchPoint: null as { customer: customer; display_name: string; lat: number; lng: number } | null,
        searchRadius: 5000,
        visibleCustomers: [] as customer[],
        allCustomers: [] as customer[],
        selectedCustomer: null as customer | null,
    },
    reducers: {
        setAllCustomers(state, action) {
            state.allCustomers = action.payload
        },
        setVisibleCustomers(state, action) {
            state.visibleCustomers = action.payload
        },
        setSearchPoint(state, action) {
            console.log('Setting search point to:', action.payload)
            state.searchPoint = action.payload
            state.selectedCustomer = null
            state.searchRadius = 5000

            if (!action.payload) {
                state.visibleCustomers = state.allCustomers
                return
            }

            const customersWithDistance = state.allCustomers
                .map((c) => ({ ...c, distance: calculateDistance(action.payload, { lat: c.latitude, lng: c.longitude }) }))
                .filter((c) => c.distance <= state.searchRadius)
                .sort((a, b) => (a.distance || 0) - (b.distance || 0))

            const filteredCustomers = customersWithDistance.filter((c) => (c.distance || 0) <= state.searchRadius)
            const sortedCustomers = sortCustomers(filteredCustomers, 'revenue')
            state.visibleCustomers = sortedCustomers
        },
        setSearchRadius(state, action) {
            state.searchRadius = action.payload

            if (state.searchPoint) {
                const customersWithDistance = state.allCustomers
                    .map((c) => ({ ...c, distance: calculateDistance(state.searchPoint!, { lat: c.latitude, lng: c.longitude }) }))
                    .filter((c) => c.distance <= action.payload)
                    .sort((a, b) => (a.distance || 0) - (b.distance || 0))

                const filteredCustomers = customersWithDistance.filter((c) => (c.distance || 0) <= action.payload)
                const sortedCustomers = sortCustomers(filteredCustomers, 'revenue')
                state.visibleCustomers = sortedCustomers
            } else {
                state.visibleCustomers = state.allCustomers
            }
        },
        setSelectedCustomer(state, action) {
            state.selectedCustomer = action.payload
        },
        reset(state) {
            state.visibleCustomers = state.allCustomers
            state.searchPoint = null
            state.searchRadius = 5000
        },
    },
})

const routeCalculationSlice = createSlice({
    name: 'routeCalculation',
    initialState: {
        startPoint: { lat: 48.67013, lng: 7.94915 } as { lat: number; lng: number },
        routes: [] as route[],
        selectedCustomer: null as customer | null,
        selectedCustomerRoute: null as route | null,
        isCalculatingCustomerRoute: false,
        selectedCustomerRouteHotelStayDuration: 0,
        selectedHotelRoute: null as route | null,
        isSearchingHotel: false,
        isCalculatingHotelRoute: false,
        currentRouteAction: null as 'stopp' | 'hotel' | null,
    },
    reducers: {
        setStartPoint(state, action) {
            state.startPoint = action.payload
        },
        setRoutes(state, action) {
            state.routes = action.payload
            console.log('Routes updated:', action.payload)
        },
        setSelectedCustomer(state, action) {
            state.selectedCustomer = action.payload
        },
        setSelectedCustomerRoute(state, action) {
            state.selectedCustomerRoute = action.payload
        },
        resetRoutes(state) {
            state.routes = []
        },
        setCurrentRouteAction(state, action) {
            state.currentRouteAction = action.payload
        },
        setSelectedHotelRoute(state, action) {
            state.selectedHotelRoute = action.payload
        },
        setSelectedCustomerRouteHotelStayDuration(state, action) {
            state.selectedCustomerRouteHotelStayDuration = action.payload
        },
        setBufferTimeForRoute(state, action) {
            const { routeId, newBufferTime } = action.payload
            const route = state.routes.find((r) => r.id === routeId)
            if (route) {
                route.bufferTime = newBufferTime
            }
        },
        removeLastRoutePoint(state, _) {
            if (state.routes.length === 0) {
                return
            }
            state.routes = state.routes.slice(0, -1)
        },
        addCustomerToRoute(state, action) {
            // Check if last route was a hotel
            if (state.routes.length > 0) {
                const lastRoute = state.routes[state.routes.length - 1]
                if (lastRoute.type === 'hotel') {
                    const updatedLastRoute = { ...lastRoute }
                    // Calculate stayDuration based on new route's departureTime
                    const stayDurationInMinutes = Math.floor((new Date(action.payload.start.departureTime).getTime() - new Date(lastRoute.end.arrivalTime).getTime()) / 60000)
                    updatedLastRoute.stayDuration = stayDurationInMinutes > 0 ? stayDurationInMinutes : 0

                    // Update the last route in the routes array
                    const updatedRoutes = [...state.routes]
                    updatedRoutes[updatedRoutes.length - 1] = updatedLastRoute
                    state.routes = updatedRoutes
                }
            }

            state.routes.push(action.payload)
            state.selectedCustomer = null
            state.selectedCustomerRoute = null
            state.currentRouteAction = null
        },
        setSelectedCustomerRouteStayDuration(state, action) {
            if (state.selectedCustomerRoute) {
                state.selectedCustomerRoute.stayDuration = action.payload
            }
        },
        setIsCalculatingCustomerRoute(state, action) {
            state.isCalculatingCustomerRoute = action.payload
        },
        setIsSearchingHotel(state, action) {
            state.isSearchingHotel = action.payload
        },
        setIsCalculatingHotelRoute(state, action) {
            state.isCalculatingHotelRoute = action.payload
        },
    },
})

const customerDetailsSlice = createSlice({
    name: 'customerDetails',
    initialState: {
        isExpanded: false,
        isMobile: false,
    },
    reducers: {
        setIsExpanded(state, action) {
            state.isExpanded = action.payload
        },
        setIsMobile(state, action) {
            state.isMobile = action.payload
        },
    },
})

export const saveBoardThunk = createAsyncThunk('board/save', async (boardData: ReactFlowJsonObject, { getState }) => {
    const state = getState() as RootState
    const { editing_board } = state.routePlanningBoard

    if (!editing_board) {
        throw new Error('No board is currently being edited. Cannot save.')
    }

    const response = await SaveRoutePlanningBoard(editing_board.name, boardData, null, editing_board.id)
    return response
})

const routePlanningBoardSlice = createSlice({
    name: 'routePlanningBoard',
    initialState: {
        tmp_customers: {} as Record<string, customer[]>,
        editing_board: null as RoutePlanningBoard | null,
        editing_board_uuid: null as string | null,
    },
    reducers: {
        setEditingBoard(state, action) {
            state.editing_board = action.payload
        },
        clearEditingBoard(state) {
            state.editing_board = null
        },
        setEditingBoardUUID(state, action) {
            state.editing_board_uuid = action.payload
        },
        setTmpCustomersForBoard(state, action) {
            const { boardId, customer } = action.payload
            if (!state.tmp_customers[boardId]) {
                state.tmp_customers[boardId] = []
            }
            state.tmp_customers[boardId].push(customer)
        },
        removeTmpCustomerFromBoard(state, action) {
            const { boardId, customerNumber } = action.payload
            if (state.tmp_customers[boardId]) {
                state.tmp_customers[boardId] = state.tmp_customers[boardId].filter((c) => c.customer_number !== customerNumber)
            }
        },
        clearTmpCustomersForBoard(state, action) {
            const { boardId } = action.payload
            state.tmp_customers[boardId] = []
        },
    },
    extraReducers: (builder) => {
        builder.addCase(saveBoardThunk.pending, (state) => {
            console.log('Saving board... (pending)')
        })
        builder.addCase(saveBoardThunk.fulfilled, (state, action) => {
            console.log('Board saved successfully:', action.payload)
        })
        builder.addCase(saveBoardThunk.rejected, (state, action) => {
            console.error('Failed to save board:', action.error)
        })
    },
})

export const imageActions = imageSlice.actions
export const sidebarActions = sidebarSlice.actions
export const notifyContainerActions = notifyContainerSlice.actions
export const allCustomersActions = allCustomersSlice.actions
export const customerMapActions = customerMapSlice.actions
export const routeCalculationActions = routeCalculationSlice.actions
export const customerDetailsActions = customerDetailsSlice.actions
export const routePlanningBoardActions = routePlanningBoardSlice.actions

export const makeStore = () =>
    configureStore({
        reducer: {
            image: imageSlice.reducer,
            sidebar: sidebarSlice.reducer,
            notifyContainer: notifyContainerSlice.reducer,
            allCustomers: allCustomersSlice.reducer,
            customerMap: customerMapSlice.reducer,
            routeCalculation: routeCalculationSlice.reducer,
            customerDetails: customerDetailsSlice.reducer,
            routePlanningBoard: routePlanningBoardSlice.reducer,
        },
    })

// Infer the type of makeStore
export type AppStore = ReturnType<typeof makeStore>
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<AppStore['getState']>
export type AppDispatch = AppStore['dispatch']
